import React, { useState, useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { RELATION_COLORS } from './familyTreeData';
import InAppChatModal from './InAppChatModal';
import InviteModal from './InviteModal';
import FamilyTreeCitationManager from './FamilyTreeCitationManager';

const {
  FiX, FiCalendar, FiHeart, FiInfo, FiGitBranch,
  FiMessageCircle, FiShare2, FiCheckCircle, FiClock, FiAlertCircle,
  FiShield, FiMail,
} = FiIcons;

/* WhatsApp SVG icon (react-icons/fa has it but let's keep zero extra deps) */
const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67-.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.125.558 4.126 1.534 5.858L.057 23.5a.5.5 0 0 0 .623.607l5.797-1.52A11.955 11.955 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.882a9.87 9.87 0 0 1-5.031-1.378l-.36-.214-3.742.981.998-3.648-.235-.374A9.86 9.86 0 0 1 2.118 12C2.118 6.533 6.533 2.118 12 2.118S21.882 6.533 21.882 12 17.467 21.882 12 21.882z"/>
  </svg>
);

const AVATAR_FALLBACK = (name = '?') =>
  `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=1E3A5F&color=C9A84C&bold=true&size=128`;

const COUSIN_RELATIONS = ['Cousin', 'Cousin (once removed)', 'Cousin (twice removed)'];

const LINK_TYPE_LABEL = {
  spouse:  'Spouse',
  parent:  'Parent / Child',
  sibling: 'Sibling',
  child:   'Child',
  cousin:  'Cousin',
};

const VERIF_STATUS_CONFIG = {
  verified:   { label: 'Verified',   color: '#059669', bg: '#ECFDF5', icon: FiCheckCircle },
  pending:    { label: 'Pending',    color: '#D97706', bg: '#FFFBEB', icon: FiClock },
  unverified: { label: 'Unverified', color: '#6B7280', bg: '#F3F4F6', icon: FiAlertCircle },
};

/* ── Smart positioning: keep panel within the container ─────────────────── */
const PANEL_W = 300;
const PANEL_H = 520;

function computeStyle(anchorPos) {
  if (!anchorPos) return { top: 16, right: 16 };

  const { x, y, containerW, containerH } = anchorPos;
  const OFFSET = 18;

  let left = x + OFFSET;
  if (left + PANEL_W > containerW - 8) left = x - PANEL_W - OFFSET;
  left = Math.max(8, left);

  let top = y - 60;
  if (top + PANEL_H > containerH - 8) top = containerH - PANEL_H - 8;
  top = Math.max(8, top);

  return { top, left };
}

const NodeDetailPanel = ({ node, connectedNodes, anchorPos, onClose, verifications, onRequestVerification, memorialId }) => {
  const [showChat,   setShowChat]   = useState(false);
  const [showInvite, setShowInvite] = useState(false);

  if (!node) return null;

  const isCousin  = COUSIN_RELATIONS.includes(node.relation);
  const relColor  = isCousin
    ? RELATION_COLORS.cousin
    : (RELATION_COLORS[node.relation?.toLowerCase()] ?? '#C9A84C');
  const headerBg  = isCousin ? '#831843' : '#1E3A5F';
  const panelStyle = computeStyle(anchorPos);

  const waMessage = encodeURIComponent(
    `Hi ${node.name}! I found your connection on Memorial Sanctuary. Would love to connect! 🕯️`
  );
  const waLink = `https://wa.me/?text=${waMessage}`;

  const nodeVerifStatus = useMemo(() => {
    const nodeLinkIds = new Set(
      (connectedNodes || []).map(cn => {
        const ids = [node.id, cn.node.id].sort();
        return `${ids[0]}-${ids[1]}-${cn.linkType}`;
      })
    );
    const nodeVerifs = (verifications || []).filter(v => nodeLinkIds.has(v.linkId));
    if (nodeVerifs.length === 0) return 'unverified';
    if (nodeVerifs.every(v => v.status === 'verified')) return 'verified';
    if (nodeVerifs.some(v => v.status === 'pending')) return 'pending';
    return 'unverified';
  }, [node, connectedNodes, verifications]);

  const overallVerif = VERIF_STATUS_CONFIG[nodeVerifStatus];
  const OverallIcon = overallVerif.icon;

  const actionBtns = [
    {
      key:     'chat',
      icon:    FiMessageCircle,
      label:   'Message',
      color:   '#1E3A5F',
      onClick: () => setShowChat(true),
    },
    {
      key:     'whatsapp',
      icon:    WhatsAppIcon,
      label:   'WhatsApp',
      color:   '#25D366',
      onClick: () => window.open(waLink, '_blank', 'noopener,noreferrer'),
    },
    {
      key:     'invite',
      icon:    FiShare2,
      label:   'Invite',
      color:   '#C9A84C',
      onClick: () => setShowInvite(true),
    },
  ];

  return (
    <>
      <AnimatePresence>
        <motion.div
          key={node.id}
          initial={{ opacity: 0, scale: 0.93, y: 12 }}
          animate={{ opacity: 1, scale: 1,    y: 0  }}
          exit={{   opacity: 0, scale: 0.93, y: 12  }}
          transition={{ type: 'spring', stiffness: 320, damping: 26 }}
          className="absolute w-[300px] bg-white rounded-[24px] shadow-2xl border-2 overflow-hidden z-20"
          style={{ ...panelStyle, borderColor: relColor }}
        >
          {/* ── Header ─────────────────────────────────────────────────── */}
          <div className="relative p-5 text-white" style={{ background: headerBg }}>

            {/* Close */}
            <button onClick={onClose}
              className="absolute top-3 right-3 text-white/40 hover:text-white transition-colors z-10">
              <SafeIcon icon={FiX} className="w-4 h-4" />
            </button>

            {/* Cousin banner */}
            {isCousin && (
              <div className="mb-3 inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest"
                style={{ background: RELATION_COLORS.cousin, color: 'white' }}>
                <SafeIcon icon={FiGitBranch} className="w-2.5 h-2.5" />
                Cousin Branch
              </div>
            )}

            {/* Verification banner */}
            <div className="mb-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest"
              style={{ background: overallVerif.bg, color: overallVerif.color }}>
              <OverallIcon className="w-2.5 h-2.5" />
              {overallVerif.label}
            </div>

            {/* Avatar + name */}
            <div className="flex items-center gap-3 mt-1">
              <div className="relative shrink-0">
                <img
                  src={node.avatar || AVATAR_FALLBACK(node.name)}
                  alt={node.name}
                  className="w-14 h-14 rounded-2xl object-cover border-2"
                  style={{ borderColor: `${relColor}60` }}
                />
                {node.isDeceased && (
                  <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-accent-gold rounded-full flex items-center justify-center text-[9px]">🕯</div>
                )}
                {isCousin && !node.isDeceased && (
                  <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full border-2 border-white flex items-center justify-center text-[8px] font-black text-white"
                    style={{ background: RELATION_COLORS.cousin }}>C</div>
                )}
              </div>
              <div className="min-w-0">
                <h4 className="font-serif text-sm leading-tight">{node.name}</h4>
                <span className="text-[9px] font-black uppercase tracking-widest mt-1 inline-block px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: `${relColor}30`, color: relColor }}>
                  {node.relation}
                </span>
              </div>
            </div>

            {/* Action labels row */}
            <div className="flex gap-1.5 mt-4">
              {actionBtns.map(btn => (
                <motion.button
                  key={`lbl-${btn.key}`}
                  onClick={btn.onClick}
                  whileHover={{ scale: 1.04 }}
                  whileTap={{  scale: 0.96 }}
                  className="flex-1 flex flex-col items-center gap-1 py-2 rounded-xl transition-all"
                  style={{ background: `${btn.color}18`, border: `1px solid ${btn.color}40` }}
                >
                  <btn.icon
                    className="w-4 h-4"
                    style={{ color: btn.color === '#25D366' ? '#25D366' : 'white' }}
                  />
                  <span className="text-[8px] font-black uppercase tracking-widest text-white/70">
                    {btn.label}
                  </span>
                </motion.button>
              ))}
            </div>
          </div>

          {/* ── Body ───────────────────────────────────────────────────── */}
          <div className="p-4 space-y-3 max-h-[280px] overflow-y-auto">
            {/* Cousin note */}
            {isCousin && (
              <div className="flex items-start gap-2 p-2.5 rounded-xl border text-[10px] leading-relaxed"
                style={{ background: '#FDF2F8', borderColor: `${RELATION_COLORS.cousin}30`, color: '#831843' }}>
                <SafeIcon icon={FiGitBranch} className="w-3 h-3 flex-shrink-0 mt-0.5" />
                <span>
                  {node.relation === 'Cousin (once removed)'
                    ? 'Shares great-grandparents with the core generation.'
                    : 'Shares grandparents — children of aunts or uncles.'}
                </span>
              </div>
            )}

            {/* Years */}
            <div className="flex items-center gap-3 text-sm text-text-muted">
              <SafeIcon icon={FiCalendar} className="text-accent-gold w-4 h-4 shrink-0" />
              <span className="font-mono text-xs">
                {node.birthYear}
                {node.deathYear ? ` – ${node.deathYear}` : ' – Present'}
              </span>
              {node.deathYear && (
                <span className="text-[9px] font-black uppercase text-accent-gold ml-auto">
                  {node.deathYear - node.birthYear} yrs
                </span>
              )}
            </div>

            {/* Bio */}
            {node.bio && (
              <div className="flex items-start gap-3">
                <SafeIcon icon={FiInfo} className="text-accent-gold w-4 h-4 shrink-0 mt-0.5" />
                <p className="text-xs text-text-muted font-light leading-relaxed">{node.bio}</p>
              </div>
            )}

            <FamilyTreeCitationManager memorialId={memorialId} node={node} />

            {/* Connections with verification status */}
            {connectedNodes?.length > 0 && (
              <div>
                <p className="text-[9px] font-black uppercase tracking-widest text-text-muted mb-2 flex items-center gap-2">
                  <SafeIcon icon={FiHeart} className="text-accent-gold w-3 h-3" />
                  Connections ({connectedNodes.length})
                </p>
                <div className="space-y-1.5">
                  {connectedNodes.map(cn => {
                    const isCnCousin = cn.linkType === 'cousin';
                    const dotColor   = RELATION_COLORS[cn.linkType] ?? '#9CA3AF';

                    const ids = [node.id, cn.node.id].sort();
                    const linkId = `${ids[0]}-${ids[1]}-${cn.linkType}`;
                    const verif = (verifications || []).find(v => v.linkId === linkId);
                    const vStatus = verif?.status ?? 'unverified';
                    const vCfg = VERIF_STATUS_CONFIG[vStatus];
                    const VIcon = vCfg.icon;

                    return (
                      <div key={cn.node.id}
                        className="flex items-center gap-2.5 p-2 rounded-xl border"
                        style={{
                          background:   isCnCousin ? '#FDF2F8' : undefined,
                          borderColor:  isCnCousin ? `${RELATION_COLORS.cousin}40` : undefined,
                        }}>
                        <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: dotColor }} />
                        <span className="text-xs font-bold text-accent-navy truncate">{cn.node.name}</span>
                        <span className="text-[9px] text-text-muted ml-auto capitalize shrink-0 font-black"
                          style={{ color: isCnCousin ? RELATION_COLORS.cousin : undefined }}>
                          {LINK_TYPE_LABEL[cn.linkType] ?? cn.linkType}
                        </span>
                        <span
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[7px] font-black uppercase tracking-widest shrink-0"
                          style={{ background: vCfg.bg, color: vCfg.color }}
                          title={`${vCfg.label}${verif?.verifiedBy ? ` by ${verif.verifiedBy}` : ''}${verif?.notes ? ` — ${verif.notes}` : ''}`}
                        >
                          <VIcon className="w-2 h-2" />
                          {vCfg.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Verification summary */}
            {connectedNodes?.length > 0 && (
              <div className="p-3 bg-brand-light/50 rounded-xl border border-brand-light">
                <div className="flex items-center gap-2 mb-2">
                  <SafeIcon icon={FiShield} className="w-3.5 h-3.5 text-accent-navy" />
                  <span className="text-[9px] font-black uppercase tracking-widest text-accent-navy">Verification</span>
                </div>
                <div className="flex gap-3 text-[8px] font-medium text-text-muted">
                  {(() => {
                    const nodeLinkIds = new Set(
                      connectedNodes.map(cn => {
                        const ids = [node.id, cn.node.id].sort();
                        return `${ids[0]}-${ids[1]}-${cn.linkType}`;
                      })
                    );
                    const nodeVerifs = (verifications || []).filter(v => nodeLinkIds.has(v.linkId));
                    const v = nodeVerifs.filter(x => x.status === 'verified').length;
                    const p = nodeVerifs.filter(x => x.status === 'pending').length;
                    const u = nodeVerifs.filter(x => x.status === 'unverified').length;
                    return (
                      <>
                        <span className="text-green-600">{v} verified</span>
                        {p > 0 && <span className="text-amber-600">{p} pending</span>}
                        {u > 0 && <span className="text-gray-500">{u} unverified</span>}
                      </>
                    );
                  })()}
                </div>
                {nodeVerifStatus === 'unverified' && onRequestVerification && (
                  <button
                    onClick={() => onRequestVerification(node)}
                    className="w-full mt-2 py-2 bg-accent-navy text-white rounded-xl text-[8px] font-black uppercase tracking-widest hover:bg-accent-gold transition-all flex items-center justify-center gap-2"
                  >
                    <SafeIcon icon={FiMail} className="w-3 h-3" />
                    Request All Verifications
                  </button>
                )}
              </div>
            )}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* ── Modals ─────────────────────────────────────────────────────── */}
      {showChat   && <InAppChatModal  node={node} onClose={() => setShowChat(false)}   />}
      {showInvite && <InviteModal     node={node} onClose={() => setShowInvite(false)} />}
    </>
  );
};

export default NodeDetailPanel;